import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@/components/auth';
import { apiClient } from '@/api/client';
import type { OrchestratorStatus, TaskDraft, OrchestrateMessage, Action } from '@/types/chat.types';

// ============================================
// Types
// ============================================

/**
 * Conversation summary for list
 */
export interface ConversationSummary {
  id: string;
  status: OrchestratorStatus;
  taskDraft: TaskDraft | null;
  /** Associated task ID (when status is 'complete') */
  taskId: string | null;
  /** Associated task publicId for navigation */
  taskPublicId: string | null;
  title: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Conversation list response
 */
interface ConversationListResponse {
  success: boolean;
  data: ConversationSummary[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    timestamp: string;
  };
}

/**
 * Full conversation with messages
 */
export interface FullConversation {
  id: string;
  status: OrchestratorStatus;
  taskDraft: TaskDraft | null;
  /** Associated task ID (when status is 'complete') */
  taskId: string | null;
  /** Associated task publicId for navigation */
  taskPublicId: string | null;
  metadata: {
    missingFields?: string[];
    assumedFields?: Array<{ field: string; value: string; confidence: 'high' | 'medium' }>;
  } | null;
  createdAt: string;
  updatedAt: string;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    metadata?: {
      status?: OrchestratorStatus;
      actions?: Action[];
      taskDraft?: TaskDraft;
    };
    createdAt: string;
  }>;
}

/**
 * Single conversation response
 */
interface ConversationResponse {
  success: boolean;
  data: FullConversation;
}

/**
 * List query options
 */
export interface ConversationListOptions {
  page?: number;
  pageSize?: number;
  status?: OrchestratorStatus;
  includeCompleted?: boolean;
  /** Filter by child profile ID */
  childId?: string;
}

// ============================================
// API Functions (using unified apiClient)
// ============================================

/**
 * Fetch conversation list
 */
async function fetchConversationList(
  options: ConversationListOptions = {}
): Promise<ConversationListResponse> {
  const params = new URLSearchParams();

  if (options.page) params.set('page', String(options.page));
  if (options.pageSize) params.set('pageSize', String(options.pageSize));
  if (options.status) params.set('status', options.status);
  if (options.includeCompleted !== undefined) {
    params.set('includeCompleted', String(options.includeCompleted));
  }
  if (options.childId) params.set('childId', options.childId);

  const { data } = await apiClient.get<ConversationListResponse>(
    `/orchestrate/conversations?${params.toString()}`
  );
  return data;
}

/**
 * Fetch single conversation by ID
 */
async function fetchConversation(conversationId: string): Promise<FullConversation> {
  const { data } = await apiClient.get<ConversationResponse>(`/orchestrate/${conversationId}`);
  return data.data;
}

/**
 * Delete conversation
 */
async function deleteConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`/orchestrate/${conversationId}`);
}

// ============================================
// Query Keys
// ============================================

export const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  list: (options: ConversationListOptions) => [...conversationKeys.lists(), options] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationKeys.details(), id] as const,
};

// ============================================
// Hooks
// ============================================

/**
 * useConversationList - Get conversation list
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useConversationList({
 *   pageSize: 20,
 *   includeCompleted: false,
 * });
 * ```
 */
export function useConversationList(options: ConversationListOptions = {}) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  return useQuery({
    queryKey: conversationKeys.list(options),
    queryFn: () => fetchConversationList(options),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    // Only enable query after auth is ready
    enabled: canMakeAuthenticatedRequests,
  });
}

/**
 * useConversation - Get single conversation details
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useConversation(conversationId);
 * ```
 */
export function useConversation(conversationId: string | undefined) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  return useQuery({
    queryKey: conversationKeys.detail(conversationId || ''),
    queryFn: () => fetchConversation(conversationId!),
    enabled: !!conversationId && canMakeAuthenticatedRequests,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * useDeleteConversation - Delete conversation
 *
 * @example
 * ```tsx
 * const { mutate: deleteConv, isPending } = useDeleteConversation();
 * deleteConv(conversationId);
 * ```
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => {
      // Invalidate list queries to refresh
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

/**
 * Transform FullConversation to format expected by useOrchestrateStream
 */
export function transformConversationToSession(conversation: FullConversation): {
  conversationId: string;
  status: OrchestratorStatus;
  taskDraft: TaskDraft | null;
  messages: OrchestrateMessage[];
  actions: Action[];
} {
  // Transform messages to OrchestrateMessage format
  const messages: OrchestrateMessage[] = conversation.messages
    .filter((msg) => msg.role !== 'system')
    .map((msg) => ({
      id: msg.id,
      role: msg.role as 'user' | 'assistant',
      content: msg.content,
      createdAt: msg.createdAt,
    }));

  // Get actions from last assistant message
  const lastAssistantMessage = [...conversation.messages]
    .reverse()
    .find((msg) => msg.role === 'assistant');
  const actions = lastAssistantMessage?.metadata?.actions || [];

  return {
    conversationId: conversation.id,
    status: conversation.status,
    taskDraft: conversation.taskDraft,
    messages,
    actions,
  };
}

export default {
  useConversationList,
  useConversation,
  useDeleteConversation,
  transformConversationToSession,
  conversationKeys,
};
